import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { Button, Modal, Spinner } from "src/components";
import { Fragment, useEffect, useState } from "react";
import SuccessIcon from "../../assets/successCircle.svg";
import CancelCircleIcon from "../../assets/cancelCircle.svg";
import { getActionPermission, getCounterByEmployee, getOrderDetailsByOrderCode2, getUinDetails, orderApproveRejectActionPermission } from "src/services/order-api";
import { useEventStatus } from "src/hooks/useEventStatus";
import { useToast } from "src/hooks/useToast";
import useOrderUinConfirmation from "src/hooks/useOrderUinConfirmation";
import FormSuccess from "../../container/FormSuccess";
import OrderApprovalForm from "../OrderDetailsTab/OrderApprovalForm";
import OrderInfoInvoiceProducts from "./OrderInfoTabCards/OrderInfoInvoiceProducts";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import { useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import { ordActions } from "src/context/order-slice";
import { useDispatch } from "react-redux";

const OrderInfoDetailsTab = () => {
  const { orderDetailsFiltered, OrderFilters } = useSelector((state: RootState) => state.ord);
  const [actionList, setActionList] = useState<string[]>([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(false);
  const [actionToPerformAfterUinVerification, setActionToPerformAfterUinVerification] = useState<string>("");
  const { currentPsr } = useSelector((state: RootState) => state.auth);
  const [uinDetails, setUinDetails] = useState<any>(null);
  const [isFetchingOrderData, setIsFetchingOrderData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [actionType, setActionType]=useState("")

  const [modalStates, setModalStates] = useState({
    approveCounter: false,
    rejectCounter: false,
    errorForm: false,
    userConfirm: false,
    order_id: 0,
  });

  const orderDetails = orderDetailsFiltered?.[0];
  const { toast } = useToast();
  const { withEventCheck } = useEventStatus({
    retries: 10,
    retryDelay: 1000,
  });
  const { orderCode } = useParams();
  const dispatch = useDispatch();

  const totalQuantity = orderDetailsFiltered?.[0]?.orderProducts?.reduce((acc: number, curr: any) => acc + curr?.quantity, 0);
  const totalBilledPrice = orderDetailsFiltered?.[0]?.orderProducts?.reduce((acc: number, curr: any) => acc + Number(curr?.billed_price), 0);

  const {
    modal,
    isOpen: checkingUINOpen,
    checkUINValidation,
    isLoading: checkingUINLoading,
  } = useOrderUinConfirmation({
    orderCode: orderDetails?.order_code,
    isApproval: true,
    divisionCode: uinDetails?.uin_exemption
      ? []
      : uinDetails?.require_uin_status && uinDetails?.is_uin_required
        ? [
          {
            counterDivisionCode: uinDetails?.company_division_code?.company_divisioncode,
            expired:
              uinDetails?.uin_last_verified_date &&
              (new Date().getTime() - new Date(uinDetails?.uin_last_verified_date).getTime()) / (1000 * 60 * 60 * 24) > 30,
          },
        ]
        : [],
    counterCode: orderDetails?.counter_code,
    counterLocationCode: orderDetails?.counterInfo?.location_code,
    divisionProductsData: [orderDetails],
    onUinVerified: () => {
      checkPermission(
        actionToPerformAfterUinVerification && actionToPerformAfterUinVerification,
        currentPsr?.emp_position_hdr?.position_code,
        orderDetails?.order_id_int,
        () => {
          if (actionToPerformAfterUinVerification === "approve") {
            setModalStates({ ...modalStates, approveCounter: true });
          } else {
            setModalStates({ ...modalStates, rejectCounter: true });
          }
        }
      );
    },
  });

  const resetForm = () => {
    setModalStates({
      approveCounter: false,
      rejectCounter: false,
      errorForm: false,
      userConfirm: false,
      order_id: 0,
    });
  };

  const closeModal = (modalName: string) => {
    setModalStates((prevState) => ({
      ...prevState,
      [modalName]: false,
    }));
    setActionToPerformAfterUinVerification("");
  };

  const checkPermission = async (action: string, position_code: string, order_id: number, callback: any) => {
    if (isCheckingPermission) return;
    setIsCheckingPermission(true);
    try {
      const response = await orderApproveRejectActionPermission(position_code, action, order_id, orderDetails?.company_code);
      setIsCheckingPermission(false);
      // if (response.status !== 200) {
      //   throw new Error();
      // }
      if (response?.data?.data?.allowed === true) {
        callback();
      } else {
        setModalStates((prev) => {
          return { ...prev, errorForm: true };
        });
      }
    } catch (error: any) {
      console.log(error);
      setIsCheckingPermission(false);
      toast({ description: "Something went wrong", variant: "destructive" });
      setModalStates((prev) => {
        return { ...prev, errorForm: true };
      });
    }
  };

  const fetchActionPermission = async (streamId?: string) => {
    if (isCheckingPermission) return;
    setIsCheckingPermission(true);

    setActionList([]);
    if (currentPsr?.emp_position_hdr?.position_code) {
      try {
        const response = await withEventCheck(
          () =>
            getActionPermission(
              currentPsr?.emp_position_hdr?.position_code,
              orderDetails?.order_id_int,
              orderDetails?.company_code
            ),
          streamId
        );
        setIsCheckingPermission(false);
        setActionList(response?.data?.data?.operation_list);
      } catch (error: any) {
        console.log(error);
        setIsCheckingPermission(false);
        resetForm();
      } finally {
        setIsCheckingPermission(false);
      }
    }
  };

  const fetchOrderId = async (orderCode: string, streamId?:string) => {
    setIsFetchingOrderData(true);
    try {
      const company = window.sessionStorage.getItem("companyFilter");
      const company_code = window.localStorage.getItem("company_code");
      const res =  await withEventCheck(
          () =>getOrderDetailsByOrderCode2(orderCode.toUpperCase(), OrderFilters?.company?.[0]?.code || company || company_code),
          streamId
        );
      dispatch(ordActions.setOrderDetailsFiltered([res?.data?.data]));
      toast({ description: "Order details fetched successfully", variant: "default" });
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message || "Something went wrong", variant: "destructive" });
    } finally {
      setIsFetchingOrderData(false);
    }
  };

  function extractLevel(status: string): string | null {
    // Regex: capture the word(s) between "Waiting for " and " Approval"
    const match = /^Waiting for (.+?) Approval$/i.exec(status?.trim() || "");
    return match ? match[1] : null;
  }

  useEffect(() => {
    if (!orderCode) return;
    if (orderDetailsFiltered?.length > 0) return;
    fetchOrderId(orderCode as string);
  }, [orderCode]);

  useEffect(() => {
    if (actionToPerformAfterUinVerification) {
      checkUINValidation();
    }
  }, [actionToPerformAfterUinVerification]);

  useEffect(() => {
    if (orderDetailsFiltered && orderDetailsFiltered.length > 0) {
      fetchActionPermission();
    }
  }, [orderDetailsFiltered]);

  return (
    <Fragment>
      {checkingUINLoading && (
        <div className="fixed inset-x-0 top-0 z-50 backdrop-blur-md transition-all duration-300 ease-in-out">
          <div className="flex items-center justify-center p-2 bg-gradient-to-r from-violet-600 via-purple-500 to-indigo-600 shadow-lg">
            <div className="flex items-center gap-4 px-4 py-1">
              <div className="relative w-6 h-6">
                <div className="absolute inset-0 rounded-full border-3 border-white/90 border-t-transparent animate-spin" />
                <div className="absolute inset-1 rounded-full bg-white/20 animate-pulse" />
              </div>
              <p className="text-sm font-medium text-white tracking-wide">
                <span className="opacity-90">Checking UIN</span>
                <span className="inline-flex ml-1 animate-pulse">...</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {isFetchingOrderData ? (
        <div className="w-full pb-20 mt-10">
          <Loader />
        </div>
      ) : (
        <div className="w-full pb-20 mt-10">
          {orderDetailsFiltered?.length > 0 && (
            <div className="flex justify-between">
              <div>
                <p className="text-xl font-semibold">Order Draft</p>
                <p className="text-lg text-[#b5b5b5]">Review and manage authorized orders</p>
              </div>
              <div className="flex items-center gap-2">
                <>
                  {isCheckingPermission && (
                    <div className="flex items-center gap-2 mr-4">
                      <Spinner size="small" />
                      <span className="text-sm">Checking permissions...</span>
                    </div>
                  )}

                    {actionList?.includes("approve") && !isCheckingPermission && (
                      <Button
                        className="border border-[#d5d5d5] rounded-lg min-w-[40px] min-h-[40px]"
                        onClick={async () => {
                          setActionType("approveCounter")
                          setModalStates({ ...modalStates, userConfirm: true });


                        }}
                        loaderSize="small"
                        isLoading={isCheckingPermission}
                        module={fineGrainedPermissionsKeys.ORDER.ORDERAPPROVE.module}
                        action={fineGrainedPermissionsKeys.ORDER.ORDERAPPROVE.action}
                      >
                        {!isLoadingData && <img src={SuccessIcon} alt="counter_details_check" className="w-full h-full mr-2" />} Approve
                      </Button>
                    )}
                    {actionList?.includes("reject") && !isCheckingPermission && (
                      <Button
                        className=" border border-[#d5d5d5] rounded-lg min-w-[40px] min-h-[40px]"
                        onClick={() => {
                          // setActionToPerformAfterUinVerification("reject");
                          // checkUINValidation();
                          setActionType("rejectCounter")
                          setModalStates({ ...modalStates, userConfirm: true });
                        }}
                        isLoading={isCheckingPermission}
                        module={fineGrainedPermissionsKeys.ORDER.ORDERREJECT.module}
                        action={fineGrainedPermissionsKeys.ORDER.ORDERREJECT.action}
                      >
                        <img src={CancelCircleIcon} alt="counter_details_cancel" className="w-full h-full mr-2" /> Reject
                      </Button>
                    )}
                </>
                <>
                  {" "}
                  <div className="flex items-center gap-2 mt-0 mr-4">
                    <p className="text-[.9rem]  border border-[#E4E4E6] rounded-lg px-2 py-1 font-semibold">
                      Total Amount: <span className="text-[#6551ff]">{totalBilledPrice.toFixed(2)}</span>
                    </p>
                    <p className="text-[.9rem] font-semibold">
                      Total Qty. <span className="text-[#6551ff]">{totalQuantity}</span>
                    </p>
                  </div>
                </>
              </div>
            </div>
          )}
          {orderDetailsFiltered?.length === 0 && (
            <div className="flex justify-center items-center h-[20vh]">
              <p className="text-lg text-[#b5b5b5]">No order details found</p>
            </div>
          )}
          {orderDetailsFiltered?.length > 0 && (
            <div
              id="scrollableDiv"
              className="h-[calc(100vh-250px)] overflow-auto overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            >
              {orderDetails?.orderProducts?.map((orders: any, index: any) => (
                <OrderInfoInvoiceProducts
                  key={orders?.order_product_id}
                  cd={orderDetails?.order_cash_discount_perc}
                  order={orderDetails}
                  product={orders}
                  index={index}
                  item_code={orders?.item_code}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <FormSuccess
        showForm={modalStates.errorForm}
        onContinue={() => resetForm()}
        onCloseModal={() => { }}
        variant="error"
        popupMessage={{
          description: "You don't have permission to perform this action",
          title: "Access Denied!",
        }}
      />

      <FormSuccess
        onCloseModal={() => {
          closeModal("userConfirm");
          resetForm();
        }}
        showForm={modalStates.userConfirm}
        onContinue={() => {
          setModalStates(prev => ({
            ...prev,
            userConfirm: false,
            ...(actionType === "rejectCounter" && { [actionType]: true })
          }));
          if (actionType === "approveCounter") {
            (async () => {
              setIsLoadingData(true);

              const uinDetails = await getUinDetails(orderDetails?.counter_ff_div_lnk_id)
              setUinDetails(uinDetails?.data?.data)
              if (uinDetails?.data?.data) {
                if (uinDetails?.data?.data?.require_uin_status === true && uinDetails?.data?.data?.is_uin_required === true) {
                  const query = `?counter_company_location_id=${orderDetails?.counter_company_location_id}&stockist=true`;
                  const counterDetails = await getCounterByEmployee(query)
                  dispatch(ordActions.setSelectedCounterDataForUin(counterDetails?.data?.data))
                }
                setActionToPerformAfterUinVerification("approve");
              }
              setIsLoadingData(false);
              // checkUINValidation();
            })()
          }
        }}
        enableBackButton={true}
        variant="error"
        popupMessage={{
          description: `You are trying to ${actionType === "approveCounter" ? "approve" : "reject"
            } the order on behalf of ${extractLevel(orderDetails?.orderStatusHdr?.order_status_desc)}`,
          title: "Confirm Action",
        }}
      />

      <Modal
        isOpen={modalStates.approveCounter}
        isHeader={true}
        onCloseModal={() => {
          closeModal("approveCounter");
          resetForm();
          setActionToPerformAfterUinVerification("");
        }}
        title="Approve Order"
      >
        <OrderApprovalForm
          type={true}
          order_id={orderDetails?.order_id_int}
          position_code={currentPsr?.emp_position_hdr?.position_code}
          counter_company_location_id={orderDetails?.counter_company_location_id}
          closeModal={closeModal}
          onSuccess={(streamId: string) => {
            fetchOrderId(orderCode as string, streamId)
          }}
        />
      </Modal>
      {modalStates.rejectCounter && (
        <Modal
          isOpen={modalStates.rejectCounter}
          isHeader={true}
          title="Reject Order"
          onCloseModal={() => {
            closeModal("rejectCounter");
            resetForm();
          }}
        >
          <OrderApprovalForm
            onSuccess={(streamId: string) => {
              fetchOrderId(orderCode as string, streamId)
            }}
            order_id={orderDetails?.order_id_int}
            position_code={currentPsr?.emp_position_hdr?.position_code}
            type={false}
            closeModal={closeModal}
          />
        </Modal>
      )}
      {checkingUINOpen ? modal : null}
    </Fragment>
  );
};

export default OrderInfoDetailsTab;
